import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signInWithPopup, OAuthProvider } from "firebase/auth";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  InputAdornment,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  AppBar,
  Toolbar,
  GlobalStyles,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { signInWithGoogle, signInWithMicrosoft } from "../firebase";
import GoogleIcon from '@mui/icons-material/Google';
//import HomeIcon from "@mui/icons-material/Home";
//import PricingIcon from "@mui/icons-material/AttachMoney";
//import ContactIcon from "@mui/icons-material/ContactMail";
//import InfoIcon from "@mui/icons-material/Info";
import MicrosoftIcon from '@mui/icons-material/Microsoft';

type LoginMethod = "email" | "google" | "microsoft";

const pushLoginEvent = (method: LoginMethod): void => {
  const analyticsWindow = window as typeof window & {
    dataLayer?: Array<Record<string, unknown>>;
  };

  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.dataLayer.push({
    event: "user_login",
    method,
    user_type: "registered",
  });
};

const getFirebaseErrorMessage = (errorCode: string): string => {
  const errorMap: Record<string, string> = {
    'auth/invalid-credential': 'Wrong email or password. Please try again.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
    'auth/email-already-in-use': 'An account already exists with this email.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/network-request-failed': 'Network error. Check your connection.',
  };
  
  return errorMap[errorCode] || 'Login failed. Please try again.';
};

const Login: React.FC = () => {
  const theme = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [mode, setMode] = useState<"login" | "register">("login");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  
  const navigate = useNavigate();

  // Go to homepage
  const goToHomepage = () => {
    window.location.href = "https://extractfast.com";
  };

  // Go to homepage section
  const goToSection = (section: string) => {
    window.location.href = `https://extractfast.com/#${section}`;
  };

  // Password strength checker
  const checkPasswordStrength = (pwd: string) => {
    let strength = 0;
    
    if (pwd.length >= 6) strength += 1;
    if (pwd.length >= 10) strength += 1;
    if (/[A-Z]/.test(pwd)) strength += 1;
    if (/[0-9]/.test(pwd)) strength += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) strength += 1;
    
    return Math.min(strength, 5);
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return "error";
    if (passwordStrength <= 2) return "warning";
    if (passwordStrength <= 3) return "info";
    return "success";
  };

  const getStrengthText = () => {
    if (passwordStrength <= 1) return "Weak";
    if (passwordStrength <= 2) return "Fair";
    if (passwordStrength <= 3) return "Good";
    return "Strong";
  };

  // Monitor password strength
  useEffect(() => {
    if (mode === "register" && password) {
      setPasswordStrength(checkPasswordStrength(password));
    } else {
      setPasswordStrength(0);
    }
  }, [password, mode]);

  // Check for redirect on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.emailVerified) {
        const redirectTo = sessionStorage.getItem("redirectAfterLogin") || "/";
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectTo);
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail.trim());
      setResetSent(true);
    } catch (error: any) {
      console.error("Password reset error:", error);
      
      if (error.code === "auth/user-not-found") {
        setResetSent(true);
      } else if (error.code === "auth/invalid-email") {
        setError("Invalid email address");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many requests. Please try again later.");
      } else {
        setError("Failed to send reset email. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      
      if (!userCredential.user.emailVerified) {
        setError("Please verify your email before logging in. Check your inbox for the verification link.");
        setLoading(false);
        return;
      }

      pushLoginEvent("email");
      
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
      } else {
        localStorage.removeItem("rememberMe");
      }
      
      const redirectTo = sessionStorage.getItem("redirectAfterLogin") || "/";
      sessionStorage.removeItem("redirectAfterLogin");
      navigate(redirectTo);
      
    } catch (error: any) {
      console.error("Login error:", error);
      setError(getFirebaseErrorMessage(error.code));
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    const user = auth.currentUser;
    if (user && !user.emailVerified) {
      try {
        await sendEmailVerification(user);
        setSuccessMessage("Verification email resent! Please check your inbox.");
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error: any) {
        setError("Failed to resend verification email. Please try again.");
      }
    }
  };

  const handleRegister = async () => {
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    if (!password || password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (passwordStrength < 2) {
      setError("Please use a stronger password (at least 10 characters with mixed case, numbers, or symbols)");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await sendEmailVerification(userCredential.user);
      
      setEmail("");
      setPassword("");
      setMode("login");
      setSuccessMessage("Verification email sent! Please check your inbox and verify your email before logging in.");
      setTimeout(() => setSuccessMessage(null), 8000);
      
    } catch (error: any) {
      console.error("Registration error:", error);
      if (error.code === "auth/email-already-in-use") {
        setError("An account already exists with this email. Please log in instead.");
        setMode("login");
      } else if (error.code === "auth/weak-password") {
        setError("Password is too weak. Please use a stronger password");
      } else if (error.code === "auth/invalid-email") {
        setError("Invalid email address");
      } else {
        setError(error.message || "Failed to register");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (mode === "login") {
      handleLogin();
    } else {
      handleRegister();
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setError(null);
    setSuccessMessage(null);
    setPassword("");
    setEmail("");
    setPasswordStrength(0);
  };

// Add Microsoft sign-in function
const signInWithMicrosoft = async () => {
  try {
    const provider = new OAuthProvider('microsoft.com');
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Microsoft sign-in error:", error);
    throw error;
  }
};



return (
  <>
    <GlobalStyles
      styles={{
        html: {
          margin: 0,
          padding: 0,
        },
        body: {
          margin: 0,
          padding: 0,
        },
        "#root": {
          margin: 0,
          padding: 0,
        },
      }}
    />

    <Box sx={{ minHeight: "100vh", bgcolor: "#fafbfd", m: 0, p: 0 }}>
      {/* Top Navigation Bar */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          bgcolor: "white",
          borderBottom: "1px solid #e5e7eb",
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: "70px",
            height: "70px",
            display: "flex",
            justifyContent: "center",
            position: "relative",
            px: { xs: 2, sm: 4 },
          }}
        >
  {/* Logo - positioned absolutely on the left */}
  <Box 
    onClick={goToHomepage} 
    sx={{ 
      display: "flex", 
      alignItems: "center", 
      gap: 1, 
      cursor: "pointer",
      position: "absolute",
      left: { xs: 2, sm: 4 }
    }}
  >
    <Box
      component="img"
      src="/ppp.png"
      alt="ExtractFast Logo"
      sx={{ width: 40, height: 40, objectFit: 'contain' }}
    />
    <Typography variant="h6" fontWeight={800}>
      <span style={{ color: '#214F80' }}>Extract</span>
      <span style={{ color: '#C6602F' }}>Fast</span>
    </Typography>
  </Box>

  {/* Navigation Links - centered */}
  <Box sx={{ display: "flex", gap: 4 }}>
    <Button 
     // startIcon={<HomeIcon />}
      onClick={goToHomepage}
      sx={{ textTransform: "none", color: "#4B5563", "&:hover": { color: "#2A9D8F" } }}
    >
      Home
    </Button>
    <Button 
      //startIcon={<InfoIcon />}
      onClick={() => goToSection("about")}
      sx={{ textTransform: "none", color: "#4B5563", "&:hover": { color: "#2A9D8F" } }}
    >
      About Us
    </Button>
    <Button 
      //startIcon={<PricingIcon />}
      onClick={() => goToSection("pricing")}
      sx={{ textTransform: "none", color: "#4B5563", "&:hover": { color: "#2A9D8F" } }}
    >
      Pricing
    </Button>
    
<Button
  // startIcon={<CareersIcon />}
  component="a"
  href="https://www.extractfast.com/careers"
  sx={{
    textTransform: "none",
    color: "#4B5563",
    "&:hover": { color: "#2A9D8F" },
  }}
>
  Careers
</Button>


    <Button 
      //startIcon={<ContactIcon />}
      onClick={() => goToSection("contact")}
      sx={{ textTransform: "none", color: "#4B5563", "&:hover": { color: "#2A9D8F" } }}
    >
      Contact Us
    </Button>
  </Box>

  {/* Empty box for balance on the right (keeps centering working) */}
  <Box sx={{ width: { xs: 40, sm: 48 }, visibility: "hidden" }} />
</Toolbar>
      </AppBar>

      {/* Login Form Container */}
      <Container maxWidth="sm" sx={{ 
        minHeight: "calc(100vh - 73px)", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center",
        py: 4
      }}>
        <Paper
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            width: "100%",
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
            boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`
          }}
        >
         

          {/* Title */}
          <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
            {mode === "login" ? "Welcome Back" : "Start Your Free Trial"}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {mode === "login"
              ? "Sign in to your ExtractFast account"
              : "14-day free trial · No credit card required"}
          </Typography>

          {/* Success Message Alert */}
          {successMessage && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setSuccessMessage(null)}
            >
              {successMessage}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
              {error.includes("verify your email") && auth.currentUser && !auth.currentUser.emailVerified && (
                <Button
                  variant="text"
                  size="small"
                  onClick={resendVerificationEmail}
                  sx={{ ml: 2, color: theme.palette.secondary.main }}
                >
                  Resend Verification Email
                </Button>
              )}
            </Alert>
          )}

          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={async () => {
              try {
                await signInWithGoogle();
                pushLoginEvent("google");
                navigate("/");
              } catch (error) {
                setError("Google sign-in failed. Please try again.");
              }
            }}
            sx={{ mb: 2 }}
          >
            Continue with Google
          </Button>
          <Button
  fullWidth
  variant="outlined"
  startIcon={<MicrosoftIcon />}
  onClick={async () => {
    try {
      await signInWithMicrosoft();
      pushLoginEvent("microsoft");
      navigate("/");
    } catch (error) {
      setError("Microsoft sign-in failed. Please try again.");
    }
  }}
  sx={{ mb: 2, py: 1.2, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
>
  Continue with Microsoft
</Button>
          <Divider sx={{ my: 1 }}>or sign in with email</Divider>
          





          {/* Form */}
          <TextField
  fullWidth
  margin="normal"
  label="Email Address"
  type="email"
  placeholder="you@company.com"
  value={email}
  onChange={(e) => setEmail(e.target.value)}
  onKeyPress={(e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }}
  disabled={loading}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <EmailIcon sx={{ color: theme.palette.secondary.main }} />
      </InputAdornment>
    ),
  }}
  sx={{
    mb: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '&:hover fieldset': {
        borderColor: theme.palette.secondary.main,
      },
    },
  }}
/>
       <TextField
  fullWidth
  margin="normal"
  label="Password"
  type={showPassword ? "text" : "password"}
  placeholder="••••••"
  value={password}
  onChange={(e) => setPassword(e.target.value)}
  onKeyPress={(e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  }}
  disabled={loading}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <LockIcon sx={{ color: theme.palette.secondary.main }} />
      </InputAdornment>
    ),
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
  }}
  sx={{
    mb: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      '&:hover fieldset': {
        borderColor: theme.palette.secondary.main,
      },
    },
  }}
/>

          {/* Password Strength Meter */}
          {mode === "register" && password && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Password strength:
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: `${getStrengthColor()}.main`,
                    fontWeight: 600
                  }}
                >
                  {getStrengthText()}
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(passwordStrength / 5) * 100} 
                color={getStrengthColor()}
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.grey[500], 0.2)
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Use 10+ chars with mix of uppercase, numbers & symbols for strong password
              </Typography>
            </Box>
          )}

          {/* Remember Me & Forgot Password - Same line */}
{mode === "login" && (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
    <FormControlLabel
      control={
        <Checkbox
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          sx={{
            color: theme.palette.secondary.main,
            '&.Mui-checked': {
              color: theme.palette.secondary.main,
            },
          }}
        />
      }
      label="Remember me"
    />
    <Button
      variant="text"
      size="small"
      onClick={() => setResetDialogOpen(true)}
      sx={{
        color: theme.palette.secondary.main,
        textTransform: "none",
      }}
    >
      Forgot password?
    </Button>
  </Box>
)}

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : (mode === "login" ? <LoginIcon /> : <PersonAddIcon />)}
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: "white",
              fontWeight: 600,
              fontSize: "1rem",
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              },
            }}
          >
            {loading
              ? (mode === "login" ? "Signing in..." : "Creating account...")
              : (mode === "login" ? "Sign In" : "Start Free Trial")}
          </Button>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary">
              OR
            </Typography>
          </Divider>

          {/* Toggle Mode */}
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {mode === "login"
                ? "Don't have an account?"
                : "Already have an account?"}
            </Typography>
            <Button
              variant="text"
              onClick={toggleMode}
              disabled={loading}
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 600,
              }}
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </Button>
          </Box>

          {/* Info Banner */}
          <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              🔒 By creating an account, you agree to our Terms of Service and Privacy Policy.
              {mode === "register" && " You'll receive a verification email to confirm your account."}
            </Typography>
          </Box>

          {/* Forgot Password Dialog */}
          <Dialog 
            open={resetDialogOpen} 
            onClose={() => {
              setResetDialogOpen(false);
              setResetSent(false);
              setForgotPasswordEmail("");
              setError(null);
            }}
            PaperProps={{
              sx: { borderRadius: 3, p: 1 }
            }}
          >
            <DialogTitle sx={{ fontWeight: 700 }}>
              Reset your password
            </DialogTitle>
            <DialogContent>
              {!resetSent ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter your email address and we'll send you a link to reset your password.
                  </Typography>
                  <TextField
                    autoFocus
                    margin="dense"
                    label="Email Address"
                    type="email"
                    fullWidth
                    variant="outlined"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    disabled={loading}
                    sx={{ mb: 1 }}
                  />
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                </>
              ) : (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <CheckCircleIcon sx={{ fontSize: 60, color: "success.main", mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Check your email
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We sent a password reset link to <strong>{forgotPasswordEmail}</strong>
                    <br />
                    Click the link in the email to create a new password.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 0 }}>
              {!resetSent ? (
                <>
                  <Button onClick={() => {
                    setResetDialogOpen(false);
                    setForgotPasswordEmail("");
                    setError(null);
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleForgotPassword} 
                    variant="contained"
                    disabled={loading || !forgotPasswordEmail}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    }}
                  >
                    {loading ? <CircularProgress size={24} /> : "Send Reset Link"}
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => {
                    setResetDialogOpen(false);
                    setResetSent(false);
                    setForgotPasswordEmail("");
                  }} 
                  variant="contained" 
                  fullWidth
                >
                  Close
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
        </Box>
  </>
);
};

export default Login;